/**
 * /transport/getting-started — Transporter onboarding walkthrough
 * ----------------------------------------------------------------
 * Anonymous-safe public page. Linked from:
 *   - /transport (directory) — "Become a transporter" CTA arrow leads here
 *   - /transport/apply — application form has a "Not sure how this works?" CTA
 *   - Transporter dashboard — first-visit welcome card (trips_completed === 0)
 *
 * Goal: walk a brand-new hauler from "I'd like to start hauling on
 * Armstrong" to "I've completed my first paid trip" with zero hidden
 * surprises. Mirrors the architecture from /memory/TRANSPORTER_PACKAGE.md
 * so the marketing matches the build.
 */
import React from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { TruckIcon as Truck, ShieldCheckIcon as ShieldCheck, CurrencyDollarIcon as CurrencyDollar, CompassIcon as Compass, CalendarIcon as Calendar, ChatCircleTextIcon as ChatCircleText, ArrowRightIcon as ArrowRight, CheckCircleIcon as CheckCircle, ListChecksIcon as ListChecks, FilePdfIcon as FilePdf, QuestionIcon as Question } from "@phosphor-icons/react";

const STEPS = [
  {
    n: 1,
    title: "Apply",
    blurb: "Submit your USDA Class B transport license number, commercial-auto insurance carrier + policy + liability amount (minimum $300,000), and basic contact info.",
    icon: ListChecks,
    cta: { to: "/transport/apply", label: "Open the application" },
    time: "10 minutes",
  },
  {
    n: 2,
    title: "Get verified",
    blurb: "We review applications within 2-3 business days. You'll receive an approval email once your license, insurance, and identity check out.",
    icon: ShieldCheck,
    time: "2–3 business days",
  },
  {
    n: 3,
    title: "Connect Stripe",
    blurb: "Approval triggers a Stripe Connect Express onboarding email. You link your bank account, verify identity, and submit your W-9. This is required before payouts can flow.",
    icon: CurrencyDollar,
    time: "15 minutes",
  },
  {
    n: 4,
    title: "Set your service area",
    blurb: "Log into your transporter dashboard and configure preferences: which U.S. states you cover, preferred lanes, breed-size familiarity, blackout dates, and how many bid invitations per day you want to receive.",
    icon: Compass,
    cta: { to: "/transporter", label: "Go to your dashboard" },
    time: "5 minutes",
  },
  {
    n: 5,
    title: "Receive bid invitations",
    blurb: "Every buyer request whose route overlaps your service area appears in your dashboard's \"Open bid invitations\" section. You also get an email alert (capped by your daily-notification preference).",
    icon: ChatCircleText,
    time: "ongoing",
  },
  {
    n: 6,
    title: "Submit competitive bids",
    blurb: "Quote your price, optionally include a pickup ETA + notes (\"crate provided\", \"insulin-trained\", etc.). You see what you net after the 10% platform fee live as you type. Buyers see your bid alongside competitors and pick a winner.",
    icon: CurrencyDollar,
    time: "2 minutes per bid",
  },
  {
    n: 7,
    title: "Win → pick up → deliver",
    blurb: "Buyer pays via Stripe Checkout (funds held in marketplace escrow). You pick up, drive, deliver. Mark the trip \"Delivered\" in your dashboard. The buyer has 48 hours to confirm — after that, escrow auto-releases to your Stripe Connect balance.",
    icon: Truck,
    time: "varies",
  },
  {
    n: 8,
    title: "Get paid",
    blurb: "Stripe transfers funds to your bank on your normal Connect payout schedule (default: every 2 business days). Lifetime earnings track in your dashboard.",
    icon: CheckCircle,
    time: "1–3 business days after release",
  },
];

const FAQ = [
  {
    q: "Do I have to accept every trip in my service area?",
    a: "No. Bids are opt-in — you only respond to the routes you want. Use blackout dates or pause your account any time you need a break.",
  },
  {
    q: "What if the buyer cancels?",
    a: "Tiered policy. ≥72h notice = full buyer refund (no fee to you). 24–72h = 25% kill fee paid to you. <24h = 50% kill fee paid to you. Pre-payment cancels (before checkout) cost nothing.",
  },
  {
    q: "What if I have to cancel?",
    a: "The buyer always gets a full refund. ≥12 hours notice = no penalty. <12 hours = +1 strike. Three strikes in a rolling 90-day window = 30-day suspension. Be honest — flag delays instead of cancelling when you can.",
  },
  {
    q: "Dog isn't ready at pickup, or the weather turns bad — now what?",
    a: "Flag the delay from the trip card (5 reasons available: weather, mechanical, unreachable, reschedule, other). Each active flag pauses auto-release +24h. Clear the flag once the issue resolves.",
  },
  {
    q: "Buyer ghosts after delivery?",
    a: "Auto-release at 48h covers you in 99% of cases. If they open a frivolous dispute, Armstrong mediates within 72 business hours. Disputes are not a popular tool — fewer than 1% of trips end up there.",
  },
  {
    q: "Is the 10% fee really all I pay?",
    a: "Yes. No monthly subscription, no per-bid charge, no Stripe Connect fee from us (Stripe takes their normal ~2.9% + 30¢ on top — same as any card processor). 10% covers verification, dispute mediation, the marketplace, and customer support.",
  },
  {
    q: "Can I work with my regular buyers exclusively?",
    a: "Yes — every completed trip auto-creates a \"preferred client\" pair. In a future release, those buyers will get first-look bid routing to you before fanning out to the wider pool. For now, the pair shows in your dashboard for relationship tracking.",
  },
  {
    q: "Where do I see the full legal agreement?",
    a: "Download the full Transporter Agreement PDF (covers exclusivity, dispute escalation, insurance obligations, and the strike system in plain English) below.",
  },
];

export default function TransportGettingStarted() {
  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="transport-getting-started">
        {/* Hero */}
        <Link to="/transport" className="text-[12px] text-[#6B6B6B] hover:text-ink">
          ← Back to directory
        </Link>
        <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight mt-3 flex items-center gap-3">
          <Truck size={36} weight="duotone" className="text-crimson" />
          Getting started
        </h1>
        <p className="text-[14px] text-[#444] mt-3 max-w-2xl leading-relaxed">
          Welcome. Here's the full picture of what it takes to join Armstrong Transport
          as an independent hauler — from application to your first paid trip. No
          shortcuts, no hidden fees, no fine print to chase down.
        </p>

        {/* Quick stats strip */}
        <div className="grid grid-cols-3 gap-3 mt-6 mb-8">
          <Stat label="Platform fee" value="10%" sub="flat per completed trip" />
          <Stat label="Minimum liability" value="$300K" sub="commercial-auto" />
          <Stat label="Escrow release" value="48h" sub="after marked delivered" />
        </div>

        {/* Steps */}
        <h2 className="font-spectral text-3xl tracking-tight mt-8 mb-3">The 8 steps</h2>
        <ol className="space-y-3" data-testid="onboarding-steps">
          {STEPS.map((s) => (
            <Step key={s.n} {...s} />
          ))}
        </ol>

        {/* Documents */}
        <h2 className="font-spectral text-3xl tracking-tight mt-12 mb-3">Read before you apply</h2>
        <div className="grid sm:grid-cols-3 gap-3 mb-8" data-testid="reference-docs">
          <DocCard
            title="Transporter Agreement"
            blurb="Binding contract you'll sign at approval. Covers escrow, cancellations, disputes, strikes, exclusivity."
            href="/api/transport/docs/agreement.pdf"
            note="Sample copy — actual signed copy emailed at approval."
          />
          <DocCard
            title="Transporter FAQ"
            blurb="15 plain-English answers to the most common questions before you apply."
            href="/api/transport/docs/faq.pdf"
          />
          <DocCard
            title="Refund Policy §9"
            blurb="Customer-facing tier table for cancellations, disputes, delay flags, and Stripe Connect fees."
            href="/refund#9"
          />
        </div>

        {/* FAQ */}
        <h2 className="font-spectral text-3xl tracking-tight mt-12 mb-3 flex items-center gap-2">
          <Question size={28} weight="duotone" />
          Quick FAQ
        </h2>
        <div className="space-y-2 mb-8" data-testid="onboarding-faq">
          {FAQ.map((item, i) => (
            <details
              key={i}
              className="border border-[#E5E5E5] bg-white rounded-xl p-4 group"
              data-testid={`faq-item-${i}`}
            >
              <summary className="cursor-pointer text-[14px] font-medium flex items-center justify-between gap-2 select-none">
                {item.q}
                <ArrowRight size={14} weight="bold" className="text-[#9A9A9A] group-open:rotate-90 transition-transform" />
              </summary>
              <p className="text-[13px] text-[#444] leading-relaxed mt-2 pt-2 border-t border-[#EAEAEA]">
                {item.a}
              </p>
            </details>
          ))}
        </div>

        {/* CTA */}
        <div className="border border-crimson/20 bg-gradient-to-br from-[#FBF7EC] to-white rounded-2xl p-6 text-center" data-testid="final-cta">
          <h3 className="font-spectral text-2xl tracking-tight">Ready to apply?</h3>
          <p className="text-[13px] text-[#444] mt-2 max-w-md mx-auto">
            10-minute form. You'll need your USDA license number, insurance details, and a photo of your rig.
          </p>
          <Link to="/transport/apply">
            <Button className="mt-4 rounded-full bg-crimson hover:bg-ink text-white font-semibold py-3 px-8" data-testid="cta-apply-final">
              Start your application <ArrowRight size={14} className="ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </AppShell>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="border border-[#E5E5E5] rounded-xl p-3 bg-[#FAFAFA] text-center">
      <p className="text-[10px] uppercase tracking-wider font-bold text-[#6B6B6B]">{label}</p>
      <p className="font-spectral text-3xl tracking-tight mt-1">{value}</p>
      <p className="text-[11px] text-[#9A9A9A] mt-0.5">{sub}</p>
    </div>
  );
}

function Step({ n, title, blurb, icon: Icon, cta, time }) {
  return (
    <li className="border border-[#E5E5E5] bg-white rounded-xl p-4 flex gap-4" data-testid={`step-${n}`}>
      <div className="flex flex-col items-center flex-shrink-0">
        <span className="flex items-center justify-center rounded-full bg-ink text-white font-spectral text-lg w-9 h-9">
          {n}
        </span>
        <Icon size={20} weight="duotone" className="text-[#9A8B6E] mt-2" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-spectral text-xl tracking-tight">{title}</p>
        <p className="text-[13px] text-[#444] mt-1 leading-relaxed">{blurb}</p>
        <div className="flex items-center gap-3 mt-2">
          <span className="text-[11px] uppercase tracking-wider font-bold text-[#9A9A9A] inline-flex items-center gap-1">
            <Calendar size={11} weight="duotone" /> {time}
          </span>
          {cta && (
            <Link
              to={cta.to}
              className="text-[12px] font-semibold text-crimson hover:text-ink underline underline-offset-2"
            >
              {cta.label} →
            </Link>
          )}
        </div>
      </div>
    </li>
  );
}

function DocCard({ title, blurb, href, note }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="border border-[#E5E5E5] bg-white rounded-xl p-4 hover:border-ink hover:bg-[#FAFAFA] transition-colors group"
      data-testid={`doc-card-${title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
    >
      <p className="font-spectral text-lg tracking-tight flex items-center gap-1.5">
        <FilePdf size={16} weight="duotone" className="text-crimson" />
        {title}
      </p>
      <p className="text-[12px] text-[#6B6B6B] mt-1 leading-relaxed">{blurb}</p>
      {note && (
        <p className="text-[10px] text-[#9A9A9A] italic mt-2">{note}</p>
      )}
      <span className="text-[11px] font-semibold text-ink group-hover:text-crimson inline-flex items-center gap-1 mt-2">
        Open <ArrowRight size={10} />
      </span>
    </a>
  );
}
